import { useMemo, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { DocChronRowFact } from './doc-chron-row-fact';
import { DocChronRowRelationships } from './doc-chron-row-relationships';
import { DocFactPicker } from './doc-fact-picker';
import { RelativityFieldSelectorPopover } from './relativity-field-selector-popover';
import { Permission } from '../../../../../../../../../../../backend/src/auth/permissions/permission';
import { DocChronologyDoc } from '../../../../../../../../../../../backend/src/matter/subset/chronology/doc-chronology/doc-chronology-doc.entity';
import { DocChronology } from '../../../../../../../../../../../backend/src/matter/subset/chronology/doc-chronology/doc-chronology.entity';
import { useRemoveDocFromChronology } from '@/api/mutations/useRemoveDocFromChronology';
import { useUpdateDocChronologyDoc } from '@/api/mutations/useUpdateDocChronologyDoc';
import { useUpdateDocChronology } from '@/api/mutations/useUpdateDocChronologyTitle';
import { useGetFilterOptions } from '@/api/queries/useGetMetadataFilterOptions';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Chip } from '@/new/components/chip';
import { EditFieldDialog } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { buildFieldsArray, renderFieldValue, RenderFieldValueOptions } from '@/new/components/metadata-display';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Popover, PopoverTrigger } from '@/new/components/shadcn/popover';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';
import { useStore } from '@/store';

interface DocChronRowProps {
  chronDoc: DocChronologyDoc;
  docChronology?: DocChronology;
  localSelectedRelativityFields: Set<string>;
  setLocalSelectedRelativityFields: (fields: Set<string>) => void;
  isCreator?: boolean;
}

export function DocChronRow({
  chronDoc,
  docChronology,
  localSelectedRelativityFields,
  setLocalSelectedRelativityFields,
  isCreator,
}: DocChronRowProps) {
  const { matterId, subsetId, docChronologyId } = useParams();

  const [isFactsExpanded, setIsFactsExpanded] = useState(false);
  const [isFactPickerOpen, setIsFactPickerOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isFieldSelectorOpen, setIsFieldSelectorOpen] = useState(false);

  const { mutate: removeDoc, isLoading: isRemoving } = useRemoveDocFromChronology();
  const { mutate: updateDoc } = useUpdateDocChronologyDoc();
  const { mutate: updateChronology } = useUpdateDocChronology();

  // Get available relativity fields for the popover
  const { data: filterOptions } = useGetFilterOptions({ matterId: matterId ?? '' });
  const hasAvailableRelativityFields = (filterOptions?.relativityFields?.fields?.length ?? 0) > 0;

  const handleRemoveDoc = () => {
    if (!matterId || !subsetId || !docChronologyId) return;

    removeDoc({
      matterId,
      subsetId,
      docChronologyId,
      docId: chronDoc.id,
    });
    setIsRemoveDialogOpen(false);
  };

  const fieldOptions: RenderFieldValueOptions = {
    badgeClassName: `text-[11px] bg-gray-100 text-gray-600 py-px border border-gray-200`,
    emptyText: 'None',
    textClassName: '',
    maxTextLength: Infinity,
  };

  // Filter relativity fields based on docChronology selection
  const selectedFields = docChronology?.relativityFields ?? [];
  const relativityFields = useMemo(() => {
    const allFields = chronDoc.doc.file.fileMetadata?.relativityFields ?? {};

    // Filter to only selected fields
    const filtered: Record<string, string | number | string[] | number[] | null> = {};
    selectedFields.forEach((key) => {
      if (allFields[key] !== undefined) {
        filtered[key] = allFields[key];
      }
    });

    return buildFieldsArray(filtered);
  }, [chronDoc.doc.file.fileMetadata?.relativityFields, selectedFields]);

  const { openReferenceDrawer } = useStore();

  return (
    <div className="relative w-full shadow-lg/1">
      <div className="relative z-20 rounded-lg border bg-white px-5.5 py-4.5 pt-3.75">
        <div className="text-xs font-medium text-gray-700">
          <div className="mb-px flex items-start justify-between gap-4">
            <div className="mb-1.5 flex items-center gap-2 text-xs">
              <button
                className="text-blue-500 hover:text-blue-600"
                onClick={() => openReferenceDrawer({ docId: chronDoc.docId })}
              >
                {chronDoc.doc.file.name}
              </button>

              {chronDoc.doc.type && <Chip className={cn(CHIP_COLOURS.type, 'text-[11px]')}>{chronDoc.doc.type}</Chip>}
            </div>

            <div className="flex items-center gap-2">
              <DocChronRowRelationships chronDoc={chronDoc} />

              <PermissionGate permission={Permission.DocChronologyEdit} orAllowed={isCreator} uiMode="disabled">
                <Dialog open={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant={'ghost'}
                      className="-mr-1 p-0"
                      onClick={(e) => {
                        if (e.shiftKey) {
                          e.preventDefault();
                          handleRemoveDoc();
                        }
                      }}
                    >
                      <Icon className="lucide--x" />
                    </Button>
                  </DialogTrigger>

                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Remove document from chronology</DialogTitle>
                      <DialogDescription>Remove a document from a document chronology</DialogDescription>
                    </DialogHeader>
                    Are you sure you want to remove this document?
                    <p className="mt-4 flex items-center gap-2 rounded-md border bg-gray-50/60 p-2 px-3">
                      <Icon className="lucide--info" />
                      Hold <kbd>shift</kbd> when removing a document to skip this confirmation
                    </p>
                    <DialogFooter>
                      <Button onClick={() => setIsRemoveDialogOpen(false)} disabled={isRemoving}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleRemoveDoc} disabled={isRemoving}>
                        Remove Document
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </PermissionGate>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <p className="flex items-center gap-2 text-lg">
              <span className={chronDoc.dateText ? '' : 'text-gray-400'}>{chronDoc.dateText || 'No Date'}</span>
              {chronDoc.title && (
                <>
                  <span className="text-sm text-gray-400/60">•</span>
                  <span>{chronDoc.title}</span>
                </>
              )}
            </p>

            <PermissionGate permission={Permission.DocChronologyEdit} uiMode="disabled">
              <EditFieldDialog
                title="Edit Document Details"
                description="Update the date and title for this document within this doc chronology."
                inputs={[
                  { label: 'Date', currentValue: chronDoc.dateText ?? '', placeholder: 'January 2024...' },
                  { label: 'Title', currentValue: chronDoc.title ?? '', placeholder: 'Email from John to Mary...' },
                ]}
                onSave={(values) => {
                  if (!matterId || !subsetId || !docChronologyId) return;
                  updateDoc({
                    matterId,
                    subsetId,
                    docChronologyId,
                    docId: chronDoc.id,
                    dateText: values['Date'] || undefined,
                    title: values['Title'] || undefined,
                  });
                }}
              />
            </PermissionGate>
          </div>
        </div>

        <p className="mt-2 text-gray-700">
          {chronDoc.summary?.split(' ').slice(0, -1).join(' ')}{' '}
          {/* span for last word and edit button so it never wraps on its own */}
          <span className="whitespace-nowrap">
            {chronDoc.summary?.split(' ').slice(-1)[0] || <span className="text-gray-400">No summary...</span>}
            <PermissionGate permission={Permission.DocChronologyEdit} uiMode="disabled">
              <EditFieldDialog
                title="Edit Summary"
                description="Update the summary for this document."
                fieldLabel="Summary"
                fieldType="textarea"
                currentValue={chronDoc.summary ?? ''}
                placeholder="Enter summary..."
                triggerClassName="inline-flex align-middle ml-0.5 text-xs -mt-0.75"
                onSave={(value) => {
                  if (!matterId || !subsetId || !docChronologyId) return;
                  updateDoc({
                    matterId,
                    subsetId,
                    docChronologyId,
                    docId: chronDoc.id,
                    summary: value || undefined,
                  });
                }}
              />
            </PermissionGate>
          </span>
        </p>

        {hasAvailableRelativityFields && (
          <div className="-m-1.5 mt-4.5 -mb-0.5 space-y-1.75 rounded-md border bg-gray-50/50 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs font-medium text-purple-500 uppercase">
              <Icon className="lucide--database mb-px" />

              <span>Relativity Metadata</span>

              {/* Popover to change which rel. fields show. Applies to all users */}
              <Popover open={isFieldSelectorOpen} onOpenChange={setIsFieldSelectorOpen}>
                <PopoverTrigger asChild>
                  <Button size="iconSm" className="mb-px ml-1 p-0.75 text-xs">
                    <Icon className="lucide--settings-2" />
                  </Button>
                </PopoverTrigger>

                <RelativityFieldSelectorPopover
                  availableFields={filterOptions?.relativityFields?.fields ?? []}
                  localSelectedFields={localSelectedRelativityFields}
                  setLocalSelectedFields={setLocalSelectedRelativityFields}
                  onFieldsChange={(fields) => {
                    if (!matterId || !subsetId || !docChronologyId) return;
                    updateChronology({
                      matterId,
                      subsetId,
                      docChronologyId,
                      title: docChronology?.title ?? 'Document Chronology',
                      relativityFields: fields,
                    });
                  }}
                  onClose={() => setIsFieldSelectorOpen(false)}
                />
              </Popover>
            </div>

            {relativityFields.map((field) => (
              <div key={field.label} className="flex gap-2">
                <span className="pt-0.75 text-xs whitespace-nowrap text-gray-500 uppercase">{field.label}:</span>
                <span className="break-all">{renderFieldValue(field, fieldOptions)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="z-10 -mt-2 rounded-b-lg border bg-gray-100 p-2 pt-4">
        {chronDoc.facts.length > 0 ? (
          <>
            <div className="mb-2 flex items-center justify-between px-1 text-xs font-medium text-gray-500">
              <div className="flex items-center gap-1.5">
                <Icon className="lucide--search mb-px text-gray-400" />

                <p>
                  <span className="text-gray-600">{chronDoc.facts.length}</span> Selected{' '}
                  {chronDoc.facts.length === 1 ? 'Fact' : 'Facts'}
                </p>

                <Button
                  variant="ghost"
                  onClick={() => setIsFactPickerOpen(true)}
                  className="gap-1 p-0 text-xs font-medium text-blue-500 hover:text-blue-600"
                >
                  Edit <Icon className="lucide--plus" />
                </Button>
              </div>

              <Button
                variant="ghost"
                onClick={() => setIsFactsExpanded(!isFactsExpanded)}
                className="gap-1 p-0 text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                {isFactsExpanded ? 'Show less' : 'Show more'}
                <Icon className={cn('lucide--chevron-down transition-transform', isFactsExpanded && 'rotate-180')} />
              </Button>
            </div>

            <div className="-mt-2 flex h-full items-start gap-2 overflow-x-auto pt-2">
              <AnimatePresence mode="popLayout">
                {[...chronDoc.facts]
                  .sort((a, b) => {
                    const dateA = a.fact.factDate ? new Date(a.fact.factDate).getTime() : 0;
                    const dateB = b.fact.factDate ? new Date(b.fact.factDate).getTime() : 0;
                    return dateA - dateB || a.id.localeCompare(b.id);
                  })
                  .map((fact) => (
                    <DocChronRowFact key={fact.id} fact={fact} chronDoc={chronDoc} isExpanded={isFactsExpanded} />
                  ))}
              </AnimatePresence>
            </div>
          </>
        ) : (
          <div className="flex gap-1.5 p-2 px-3 pt-1.5 text-xs">
            <span className="text-gray-500">No Facts selected</span>
            <Button
              variant="ghost"
              onClick={() => setIsFactPickerOpen(true)}
              className="gap-1 p-0 text-xs font-medium text-blue-500 hover:text-blue-600"
            >
              Add Facts from Document <Icon className="lucide--plus" />
            </Button>
          </div>
        )}
      </div>

      <DocFactPicker chronDoc={chronDoc} open={isFactPickerOpen} onOpenChange={setIsFactPickerOpen} />
    </div>
  );
}
